import { useMutation } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { useTranslation } from 'react-i18next';

import { DownloadIcon, DriveIcon } from '../../assets';
import {
  GENERATE_PROPOSAL_DOC,
  GENERATE_PROPOSAL_PDF,
  SELECT_PROPOSAL_PACK,
} from '../../graphql/proposal/mutation';
import { GET_PROPOSAL } from '../../graphql/proposal/query';
import { useNotification } from '../../hooks/useNotification';
import type {
  ProposalData,
  ProposalInfluencer,
  ProposalPack,
  ResGenerateProposalDoc,
  ResGenerateProposalPdf,
  ResSelectProposalPack,
} from '../../types/proposal';
import { downloadDocumentClick } from '../../utils/utils';
import { CampaignInfluencers } from '../campaigns/Sidebar/CampaignInfluencers';
import { CampaignPrice } from '../campaigns/Sidebar/CampaignPrice';
import { HiredPlatforms } from '../campaigns/Sidebar/HiredPlatforms';
import { Button } from '../common/Button';
import { Warning } from '../common/Warning';
import { BrandImage } from '../images/BrandImage';
import ProposalStatusDropdown from '../inputs/ProposalStatusDropdown';

function packLabel(pack: ProposalPack): string {
  return pack.name || `Pack ${pack.position + 1}`;
}

function PackSelectorRow({
  influencer,
  proposalId,
  refetch,
}: {
  influencer: ProposalInfluencer;
  proposalId: string;
  refetch: () => void;
}) {
  const { t } = useTranslation('common');
  const packs = (influencer.packs || [])
    .slice()
    .sort((a, b) => a.position - b.position);
  const hasPacks = packs.length > 0;
  const selectedPackId = influencer.selected_pack_id || null;
  const { notifyWarning } = useNotification();

  const [selectProposalPack, { loading: selecting }] =
    useMutation<ResSelectProposalPack>(SELECT_PROPOSAL_PACK, {
      refetchQueries: [
        {
          query: GET_PROPOSAL,
          variables: { proposalId },
        },
      ],
      onError: (error) => {
        notifyWarning(error.message);
      },
    });

  const handleSelect = (packId: string) => {
    if (selecting) {
      return;
    }
    const nextValue = selectedPackId === packId ? null : packId;
    selectProposalPack({
      variables: { proposalUserId: influencer.id, packId: nextValue },
      onCompleted: () => refetch(),
    });
  };

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-white text-[11px] font-semibold uppercase tracking-widest">
        {influencer.name || influencer.id}
      </span>
      {hasPacks ? (
        <div className="flex flex-col gap-1">
          {packs.map((pack) => {
            const checked = selectedPackId === pack.id;
            return (
              <label
                key={pack.id}
                className={`flex flex-row items-center gap-2 px-2 py-1 rounded-[4px] cursor-pointer text-[12px] font-normal ${
                  checked
                    ? 'bg-influentia-yellow text-dark-blue'
                    : 'bg-influentia-black text-white hover:bg-influentia-light-blue hover:text-dark-blue'
                }`}
              >
                <input
                  type="radio"
                  name={`selected-pack-${influencer.id}`}
                  className="accent-influentia-light-blue"
                  checked={checked}
                  disabled={selecting}
                  onChange={() => handleSelect(pack.id)}
                />
                <span className="flex-1 truncate">{packLabel(pack)}</span>
              </label>
            );
          })}
        </div>
      ) : (
        <span className="text-gray-400 text-[11px] italic">
          {t('proposals.noPacksAvailable')}
        </span>
      )}
    </div>
  );
}

export function ProposalSidebarCard({
  proposal,
  refetch,
}: {
  proposal: ProposalData;
  refetch: () => void;
}) {
  const { t } = useTranslation('common');
  const { getAccessTokenSilently } = useAuth0();
  const { notifySuccess, notifyError } = useNotification();
  const hasPdf = Boolean(proposal.pdf_url);
  const hasDoc = Boolean(proposal.drive_document_url);
  const [generateProposalPdf, { loading: generatingPdf }] =
    useMutation<ResGenerateProposalPdf>(GENERATE_PROPOSAL_PDF, {
      refetchQueries: [
        {
          query: GET_PROPOSAL,
          variables: { proposalId: proposal.id },
        },
      ],
      onCompleted: (response) =>
        response.generateProposalPdf.code === 200
          ? notifySuccess(t('proposals.generateProposalSuccess'))
          : notifyError(t('proposals.generateProposalError')),
      onError: () => notifyError(t('proposals.generateProposalError')),
    });

  // Independent mutation for the vertical proposal Doc. Has its own loading
  // state so the Slides button and the Docs button can be clicked
  // independently without loading indicators getting stuck together.
  const [generateProposalDoc, { loading: generatingDoc }] =
    useMutation<ResGenerateProposalDoc>(GENERATE_PROPOSAL_DOC, {
      refetchQueries: [
        {
          query: GET_PROPOSAL,
          variables: { proposalId: proposal.id },
        },
      ],
      onCompleted: (response) =>
        response.generateProposalDoc.code === 200
          ? notifySuccess(t('proposals.generateVerticalDocSuccess'))
          : notifyError(t('proposals.generateVerticalDocError')),
      onError: () => notifyError(t('proposals.generateVerticalDocError')),
    });

  const handleGenerateProposalPdf = () => {
    generateProposalPdf({ variables: { proposalId: proposal.id } });
  };

  const handleGenerateProposalDoc = () => {
    generateProposalDoc({ variables: { proposalId: proposal.id } });
  };

  const handleDownloadProposalPdf = async () => {
    if (!proposal.pdf_url) {
      return;
    }

    await downloadDocumentClick(
      proposal.pdf_url,
      await getAccessTokenSilently(),
      undefined,
      `${proposal.name}.pdf`,
    );
  };

  const activeInfluencers = proposal.ProposalUsers.filter(
    (u) => u.status !== 'rejected',
  );

  return (
    <div className="bg-dark-blue 2xl:p-5 p-4 flex flex-col gap-4 rounded-10 lg:w-[420px] flex-none h-fit">
      <div className="bg-influentia-black rounded-10 p-4 flex flex-row gap-4 items-center">
        <BrandImage
          image={proposal.brand_profile_picture || undefined}
          size="size-[76px]"
        />
        <div className="flex flex-col gap-1 w-full">
          <span className="text-white font-bold">
            {proposal.brand_name || '-'}
          </span>
          <span className="text-gray-400 text-sm">{proposal.month || '-'}</span>
          <ProposalStatusDropdown
            defaultStatus={proposal.status}
            proposalId={proposal.id}
            refetch={refetch}
          />
        </div>
      </div>
      <CampaignPrice
        price={proposal.money ?? 0}
        currency={proposal.currency ?? undefined}
      />
      <HiredPlatforms
        platforms={Array.from(
          new Set(
            activeInfluencers
              .flatMap((u) => (u.actions ?? []).map((a) => a.platform))
              .filter(Boolean),
          ),
        )}
      />
      <CampaignInfluencers
        influencers={activeInfluencers.map((u) => ({
          id: u.id,
          name: u.name || u.id,
        }))}
      />
      {proposal.accountManagers && proposal.accountManagers.length > 0 && (
        <div className="flex flex-row gap-[6px]">
          <div className="rounded-full bg-white flex justify-center items-center size-[28px] text-influentia-black flex-none">
            <svg
              className="size-4"
              fill="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="text-influentia-light-blue font-medium text-[10px]">
              {t('proposals.fields.accountManagers')}
            </span>
            <div className="flex flex-col gap-1">
              {proposal.accountManagers.map((am) => (
                <span key={am.id} className="font-medium text-white text-sm">
                  {am.name}
                </span>
              ))}
            </div>
          </div>
        </div>
      )}
      {activeInfluencers.length > 0 && (
        <div className="flex flex-col gap-3">
          <span className="text-white font-semibold text-xs uppercase tracking-widest">
            {t('proposals.packSelection')}
          </span>
          <div className="flex flex-col gap-3">
            {activeInfluencers.map((influencer) => (
              <PackSelectorRow
                key={influencer.id}
                influencer={influencer}
                proposalId={proposal.id}
                refetch={refetch}
              />
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-col gap-3">
        <span className="text-white font-semibold text-xs uppercase tracking-widest">
          {t('proposals.title')}
        </span>
        {hasPdf && (
          <div className="flex flex-col gap-1.5">
            <span className="text-gray-400 text-[10px] uppercase tracking-wide">
              {t('proposals.completeProposalGroup')}
            </span>
            <div className="flex flex-row gap-2 flex-wrap">
              {proposal.pdf_url && (
                <Button
                  id="downloadProposalPdfSidebar"
                  style="light-blue"
                  text={t('proposals.downloadPdf')}
                  customClassnames="flex-1 justify-center hover:!bg-influentia-black hover:!text-white"
                  iconLeft={<DownloadIcon className="size-4" />}
                  onClick={handleDownloadProposalPdf}
                />
              )}
              {proposal.drive_url && (
                <a
                  href={proposal.drive_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex flex-row items-center justify-center gap-1.5 px-3 py-1.5 rounded-[4px] text-sm font-normal bg-influentia-yellow text-dark-blue hover:opacity-80"
                >
                  <DriveIcon className="size-4" />
                  {t('proposals.openInDrive')}
                </a>
              )}
            </div>
          </div>
        )}
        {hasDoc && proposal.drive_document_url && (
          <div className="flex flex-col gap-1.5">
            <span className="text-gray-400 text-[10px] uppercase tracking-wide">
              {t('proposals.verticalProposalGroup')}
            </span>
            <div className="flex flex-row gap-2 flex-wrap">
              <a
                href={proposal.drive_document_url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex flex-row items-center justify-center gap-1.5 px-3 py-1.5 rounded-[4px] text-sm font-normal bg-influentia-yellow text-dark-blue hover:opacity-80"
              >
                <DriveIcon className="size-4" />
                {t('proposals.openVerticalDoc')}
              </a>
            </div>
          </div>
        )}
        {proposal.missing_from_ppt && proposal.missing_from_ppt.length > 0 && (
          <Warning
            warning={t('proposals.missingFromPpt', {
              names: proposal.missing_from_ppt.join(', '),
            })}
          />
        )}
        <Button
          id="generateProposal"
          style="light-blue"
          text={
            hasPdf
              ? t('proposals.regenerateProposal')
              : t('proposals.generateProposal')
          }
          customClassnames="w-full justify-center hover:!bg-influentia-black hover:!text-white"
          onClick={handleGenerateProposalPdf}
          loading={generatingPdf}
          disabled={generatingPdf || generatingDoc}
        />
        <Button
          id="generateProposalDoc"
          style="light-blue"
          text={
            hasDoc
              ? t('proposals.regenerateVerticalDoc')
              : t('proposals.generateVerticalDoc')
          }
          customClassnames="w-full justify-center hover:!bg-influentia-black hover:!text-white"
          onClick={handleGenerateProposalDoc}
          loading={generatingDoc}
          disabled={generatingDoc || generatingPdf}
        />
      </div>
    </div>
  );
}
